import {
  ALStream,
  EnglishTestType,
  FundingSource,
  Gender,
  HEALTH_DECLARATION_CONDITIONS,
  MaritalStatus,
  OlAlType,
  StudentListItem,
  SubjectResult,
  VisaType,
} from '../../../types/student';
import { CreateStudentPayload } from '../../../types/student';

/** '' represents "not chosen yet" for every select/number field — MUI Select/TextField
 * need a defined value to stay controlled, and CreateStudentPayload fields are all optional. */
export interface WizardValues {
  // Step 1 — Application Details
  branchId: number | '';
  preferredUniversityId: number | '';
  preferredProgramId: number | '';
  // Student Application correction: free text, replacing the old
  // Faculty/Semester/Intake-dropdown fields (preferredFacultyId,
  // preferredIntakeId, semester) — those columns still exist and keep
  // whatever a historical record already has, but this wizard no longer
  // collects or overwrites them.
  preferredIntakeLabel: string;
  academicYear: string;

  // Step 2 — Personal Details
  firstName: string;
  lastName: string;
  otherNames: string;
  preferredName: string;
  gender: Gender | '';
  dateOfBirth: string;
  nationalityCountryId: number | '';
  nicNumber: string;
  passportNumber: string;
  passportIssueDate: string;
  passportExpiryDate: string;
  countryOfBirthId: number | '';
  maritalStatus: MaritalStatus | '';
  email: string;
  phone: string;
  whatsappNumber: string;
  telephone: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;

  // Step 3 — Academic Details (O/L, A/L)
  schoolName: string;
  olType: OlAlType | '';
  olYear: number | '';
  alType: OlAlType | '';
  alYear: number | '';
  alStream: ALStream | '';
  overallGpa: number | '';

  // Step 5 — Preferred Study
  countryId: number | '';
  preferredQualificationLevelId: number | '';
  preferredCity: string;
  alternativeCountryId: number | '';
  alternativeUniversityId: number | '';
  scholarshipRequired: boolean;
  englishTestType: EnglishTestType | '';
  englishTestScore: string;
  englishTestDate: string;

  // Step 6 — Parents/Guardian
  fatherName: string;
  fatherOccupation: string;
  fatherOrganization: string;
  fatherOfficePhone: string;
  fatherPhone: string;
  fatherWhatsapp: string;
  fatherEmail: string;
  fatherAddress: string;
  motherName: string;
  motherOccupation: string;
  motherOrganization: string;
  motherOfficePhone: string;
  motherPhone: string;
  motherWhatsapp: string;
  motherEmail: string;
  motherAddress: string;
  guardianName: string;
  guardianRelationship: string;
  guardianPhone: string;
  guardianWhatsapp: string;
  guardianEmail: string;
  guardianAddress: string;

  // Step 7 — Visa History
  visaAppliedBefore: boolean;
  visaRefused: boolean;
  visaRefusedCountryId: number | '';
  visaType: VisaType | '';
  visaRefusalReason: string;
  visaComments: string;
  travelHistory: string;

  // Step 8 — Financial Information
  monthlyIncome: number | '';
  annualIncome: number | '';
  fundingMethod: FundingSource | '';
  sponsorDetails: string;

  // Step 10 — Declaration
  studentAgreementAccepted: boolean;
  privacyConsentAccepted: boolean;
  marketingConsentAccepted: boolean;
  studentSignatureName: string;
  guardianSignatureName: string;

  // Health Declaration Form (Step 10) — toggle default off; '' on an
  // individual answer means "not yet answered" (the same not-chosen-yet
  // sentinel convention as every other field here).
  healthDeclarationRequired: boolean;
  healthDeclarationAnswers: HealthDeclarationAnswerDraft[];
  guardianPassportOrNic: string;
}

export interface HealthDeclarationAnswerDraft {
  key: string;
  answer: 'YES' | 'NO' | '';
  statement: string;
}

export interface SubjectResultDraft {
  localId: string;
  subject: string;
  grade: string;
}

export const emptySubjectResultDraft = (): SubjectResultDraft => ({
  localId: crypto.randomUUID(),
  subject: '',
  grade: '',
});

export const resultsToDrafts = (results: SubjectResult[] | null): SubjectResultDraft[] =>
  (results ?? []).map((r) => ({ localId: crypto.randomUUID(), subject: r.subject, grade: r.grade ?? '' }));

const draftsToResults = (drafts: SubjectResultDraft[]): SubjectResult[] | undefined => {
  const results = drafts.filter((d) => d.subject.trim()).map((d) => ({ subject: d.subject.trim(), grade: d.grade.trim() || undefined }));
  return results.length > 0 ? results : undefined;
};

export const emptyWizardValues: WizardValues = {
  branchId: '',
  preferredUniversityId: '',
  preferredProgramId: '',
  preferredIntakeLabel: '',
  academicYear: '',

  firstName: '',
  lastName: '',
  otherNames: '',
  preferredName: '',
  gender: '',
  dateOfBirth: '',
  nationalityCountryId: '',
  nicNumber: '',
  passportNumber: '',
  passportIssueDate: '',
  passportExpiryDate: '',
  countryOfBirthId: '',
  maritalStatus: '',
  email: '',
  phone: '',
  whatsappNumber: '',
  telephone: '',
  address: '',
  city: '',
  postalCode: '',
  province: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelationship: '',

  schoolName: '',
  olType: '',
  olYear: '',
  alType: '',
  alYear: '',
  alStream: '',
  overallGpa: '',

  countryId: '',
  preferredQualificationLevelId: '',
  preferredCity: '',
  alternativeCountryId: '',
  alternativeUniversityId: '',
  scholarshipRequired: false,
  englishTestType: '',
  englishTestScore: '',
  englishTestDate: '',

  fatherName: '',
  fatherOccupation: '',
  fatherOrganization: '',
  fatherOfficePhone: '',
  fatherPhone: '',
  fatherWhatsapp: '',
  fatherEmail: '',
  fatherAddress: '',
  motherName: '',
  motherOccupation: '',
  motherOrganization: '',
  motherOfficePhone: '',
  motherPhone: '',
  motherWhatsapp: '',
  motherEmail: '',
  motherAddress: '',
  guardianName: '',
  guardianRelationship: '',
  guardianPhone: '',
  guardianWhatsapp: '',
  guardianEmail: '',
  guardianAddress: '',

  visaAppliedBefore: false,
  visaRefused: false,
  visaRefusedCountryId: '',
  visaType: '',
  visaRefusalReason: '',
  visaComments: '',
  travelHistory: '',

  monthlyIncome: '',
  annualIncome: '',
  fundingMethod: '',
  sponsorDetails: '',

  studentAgreementAccepted: false,
  privacyConsentAccepted: false,
  marketingConsentAccepted: false,
  studentSignatureName: '',
  guardianSignatureName: '',

  healthDeclarationRequired: false,
  healthDeclarationAnswers: HEALTH_DECLARATION_CONDITIONS.map((c) => ({ key: c.key, answer: '', statement: '' })),
  guardianPassportOrNic: '',
};

const num = (v: number | '') => (v === '' ? undefined : v);
const str = (v: string) => (v.trim() === '' ? undefined : v.trim());

/** Builds the API payload from wizard state, converting '' sentinels to undefined
 * (z.coerce.date() rejects '' as an invalid date rather than "not provided"). */
export const buildStudentPayload = (
  values: WizardValues,
  olResults: SubjectResultDraft[],
  alResults: SubjectResultDraft[],
): CreateStudentPayload => ({
  branchId: num(values.branchId),
  preferredUniversityId: num(values.preferredUniversityId),
  preferredProgramId: num(values.preferredProgramId),
  preferredIntakeLabel: str(values.preferredIntakeLabel),
  academicYear: str(values.academicYear),

  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  otherNames: str(values.otherNames),
  preferredName: str(values.preferredName),
  gender: values.gender || undefined,
  dateOfBirth: str(values.dateOfBirth),
  nationalityCountryId: num(values.nationalityCountryId),
  nicNumber: str(values.nicNumber),
  passportNumber: str(values.passportNumber),
  passportIssueDate: str(values.passportIssueDate),
  passportExpiryDate: str(values.passportExpiryDate),
  countryOfBirthId: num(values.countryOfBirthId),
  maritalStatus: values.maritalStatus || undefined,
  email: str(values.email),
  phone: values.phone.trim(),
  whatsappNumber: str(values.whatsappNumber),
  telephone: str(values.telephone),
  address: str(values.address),
  city: str(values.city),
  postalCode: str(values.postalCode),
  province: str(values.province),
  emergencyContactName: str(values.emergencyContactName),
  emergencyContactPhone: str(values.emergencyContactPhone),
  emergencyContactRelationship: str(values.emergencyContactRelationship),

  schoolName: str(values.schoolName),
  olType: values.olType || undefined,
  olYear: num(values.olYear),
  olResults: draftsToResults(olResults),
  alType: values.alType || undefined,
  alYear: num(values.alYear),
  alStream: values.alStream || undefined,
  alResults: draftsToResults(alResults),
  overallGpa: num(values.overallGpa),

  countryId: num(values.countryId),
  preferredQualificationLevelId: num(values.preferredQualificationLevelId),
  preferredCity: str(values.preferredCity),
  alternativeCountryId: num(values.alternativeCountryId),
  alternativeUniversityId: num(values.alternativeUniversityId),
  scholarshipRequired: values.scholarshipRequired,
  englishTestType: values.englishTestType || undefined,
  englishTestScore: str(values.englishTestScore),
  englishTestDate: str(values.englishTestDate),

  fatherName: str(values.fatherName),
  fatherOccupation: str(values.fatherOccupation),
  fatherOrganization: str(values.fatherOrganization),
  fatherOfficePhone: str(values.fatherOfficePhone),
  fatherPhone: str(values.fatherPhone),
  fatherWhatsapp: str(values.fatherWhatsapp),
  fatherEmail: str(values.fatherEmail),
  fatherAddress: str(values.fatherAddress),
  motherName: str(values.motherName),
  motherOccupation: str(values.motherOccupation),
  motherOrganization: str(values.motherOrganization),
  motherOfficePhone: str(values.motherOfficePhone),
  motherPhone: str(values.motherPhone),
  motherWhatsapp: str(values.motherWhatsapp),
  motherEmail: str(values.motherEmail),
  motherAddress: str(values.motherAddress),
  guardianName: str(values.guardianName),
  guardianRelationship: str(values.guardianRelationship),
  guardianPhone: str(values.guardianPhone),
  guardianWhatsapp: str(values.guardianWhatsapp),
  guardianEmail: str(values.guardianEmail),
  guardianAddress: str(values.guardianAddress),

  visaAppliedBefore: values.visaAppliedBefore,
  visaRefused: values.visaRefused,
  visaRefusedCountryId: num(values.visaRefusedCountryId),
  visaType: values.visaType || undefined,
  visaRefusalReason: str(values.visaRefusalReason),
  visaComments: str(values.visaComments),
  travelHistory: str(values.travelHistory),

  monthlyIncome: num(values.monthlyIncome),
  annualIncome: num(values.annualIncome),
  fundingMethod: values.fundingMethod || undefined,
  sponsorDetails: str(values.sponsorDetails),

  studentAgreementAccepted: values.studentAgreementAccepted,
  privacyConsentAccepted: values.privacyConsentAccepted,
  marketingConsentAccepted: values.marketingConsentAccepted,
  termsAcceptedAt: values.studentAgreementAccepted ? new Date().toISOString() : undefined,
  studentSignatureName: str(values.studentSignatureName),
  studentSignatureAt: str(values.studentSignatureName) ? new Date().toISOString() : undefined,
  guardianSignatureName: str(values.guardianSignatureName),
  guardianSignatureAt: str(values.guardianSignatureName) ? new Date().toISOString() : undefined,

  healthDeclarationRequired: values.healthDeclarationRequired,
  healthDeclarationAnswers: values.healthDeclarationAnswers
    .filter((a) => a.answer)
    .map((a) => ({
      key: a.key,
      answer: a.answer as 'YES' | 'NO',
      statement: a.answer === 'NO' ? str(a.statement) : undefined,
    })),
  guardianPassportOrNic: str(values.guardianPassportOrNic),
});

/** Populates wizard state when resuming an existing draft student. */
export const studentToWizardValues = (student: StudentListItem): WizardValues => ({
  branchId: student.branch?.id ?? '',
  preferredUniversityId: student.preferredUniversity?.id ?? '',
  preferredProgramId: student.preferredProgram?.id ?? '',
  // Falls back to the historical Intake row's name so resuming an
  // old draft doesn't show a blank field where a value already existed —
  // never invented if genuinely never set.
  preferredIntakeLabel: student.preferredIntakeLabel ?? student.preferredIntake?.name ?? '',
  // Campaign removed from this step's UI — student.campaignId is left
  // untouched on the record (never read into wizard state, never sent back
  // in the payload, so an existing value is neither shown nor overwritten).
  academicYear: student.academicYear ?? '',

  firstName: student.firstName,
  lastName: student.lastName,
  otherNames: student.otherNames ?? '',
  preferredName: student.preferredName ?? '',
  gender: student.gender ?? '',
  dateOfBirth: student.dateOfBirth ? student.dateOfBirth.slice(0, 10) : '',
  nationalityCountryId: student.nationalityCountry?.id ?? '',
  nicNumber: student.nicNumber ?? '',
  passportNumber: student.passportNumber ?? '',
  passportIssueDate: student.passportIssueDate ? student.passportIssueDate.slice(0, 10) : '',
  passportExpiryDate: student.passportExpiryDate ? student.passportExpiryDate.slice(0, 10) : '',
  countryOfBirthId: student.countryOfBirth?.id ?? '',
  maritalStatus: student.maritalStatus ?? '',
  email: student.email ?? '',
  phone: student.phone,
  whatsappNumber: student.whatsappNumber ?? '',
  telephone: student.telephone ?? '',
  address: student.address ?? '',
  city: student.city ?? '',
  postalCode: student.postalCode ?? '',
  province: student.province ?? '',
  emergencyContactName: student.emergencyContactName ?? '',
  emergencyContactPhone: student.emergencyContactPhone ?? '',
  emergencyContactRelationship: student.emergencyContactRelationship ?? '',

  schoolName: student.schoolName ?? '',
  olType: student.olType ?? '',
  olYear: student.olYear ?? '',
  alType: student.alType ?? '',
  alYear: student.alYear ?? '',
  alStream: student.alStream ?? '',
  overallGpa: student.overallGpa ? Number(student.overallGpa) : '',

  countryId: student.country?.id ?? '',
  preferredQualificationLevelId: student.preferredQualificationLevel?.id ?? '',
  preferredCity: student.preferredCity ?? '',
  alternativeCountryId: student.alternativeCountry?.id ?? '',
  alternativeUniversityId: student.alternativeUniversity?.id ?? '',
  // Expected Budget/Currency removed from this step's UI — student.budget/
  // budgetCurrency are left untouched on the record (never read into
  // wizard state, never sent back in the payload, so an existing value is
  // neither shown nor overwritten).
  scholarshipRequired: student.scholarshipRequired,
  englishTestType: student.englishTestType ?? '',
  englishTestScore: student.englishTestScore ?? '',
  englishTestDate: student.englishTestDate ? student.englishTestDate.slice(0, 10) : '',

  fatherName: student.fatherName ?? '',
  fatherOccupation: student.fatherOccupation ?? '',
  fatherOrganization: student.fatherOrganization ?? '',
  fatherOfficePhone: student.fatherOfficePhone ?? '',
  fatherPhone: student.fatherPhone ?? '',
  fatherWhatsapp: student.fatherWhatsapp ?? '',
  fatherEmail: student.fatherEmail ?? '',
  fatherAddress: student.fatherAddress ?? '',
  motherName: student.motherName ?? '',
  motherOccupation: student.motherOccupation ?? '',
  motherOrganization: student.motherOrganization ?? '',
  motherOfficePhone: student.motherOfficePhone ?? '',
  motherPhone: student.motherPhone ?? '',
  motherWhatsapp: student.motherWhatsapp ?? '',
  motherEmail: student.motherEmail ?? '',
  motherAddress: student.motherAddress ?? '',
  guardianName: student.guardianName ?? '',
  guardianRelationship: student.guardianRelationship ?? '',
  guardianPhone: student.guardianPhone ?? '',
  guardianWhatsapp: student.guardianWhatsapp ?? '',
  guardianEmail: student.guardianEmail ?? '',
  guardianAddress: student.guardianAddress ?? '',
  // Financial sponsor toggles removed from this step's UI for the same
  // reason as above — student.fatherIsSponsor/motherIsSponsor/
  // guardianIsSponsor are left untouched on the record.

  visaAppliedBefore: student.visaAppliedBefore ?? false,
  visaRefused: student.visaRefused ?? false,
  visaRefusedCountryId: student.visaRefusedCountry?.id ?? '',
  visaType: student.visaType ?? '',
  visaRefusalReason: student.visaRefusalReason ?? '',
  visaComments: student.visaComments ?? '',
  travelHistory: student.travelHistory ?? '',

  monthlyIncome: student.monthlyIncome ? Number(student.monthlyIncome) : '',
  annualIncome: student.annualIncome ? Number(student.annualIncome) : '',
  fundingMethod: student.fundingMethod ?? '',
  sponsorDetails: student.sponsorDetails ?? '',

  studentAgreementAccepted: student.studentAgreementAccepted,
  privacyConsentAccepted: student.privacyConsentAccepted,
  marketingConsentAccepted: student.marketingConsentAccepted,
  studentSignatureName: student.studentSignatureName ?? '',
  guardianSignatureName: student.guardianSignatureName ?? '',

  healthDeclarationRequired: student.healthDeclarationRequired,
  // Rebuilds the full canonical condition list every time, defensively —
  // covers a historical record with no Health Declaration data at all
  // (every answer defaults to '' — nothing crashes, nothing is invented)
  // exactly as safely as one with a complete, previously-saved answer set.
  healthDeclarationAnswers: HEALTH_DECLARATION_CONDITIONS.map((c) => {
    const existing = student.healthDeclarationAnswers?.find((a) => a.key === c.key);
    return { key: c.key, answer: existing?.answer ?? '', statement: existing?.statement ?? '' };
  }),
  guardianPassportOrNic: student.guardianPassportOrNic ?? '',
});

/** Mirrors the backend's finalizeStudent completeness gate (students.service.ts)
 * so the "Next" button can block leaving the Declaration step before it
 * would fail server-side anyway — every condition needs an answer, a "No"
 * needs a statement, and the student's own signature/passport number (the
 * identifying fields for this declaration) must be present. Always true
 * when the toggle is off, since nothing is required in that case. */
export const isHealthDeclarationComplete = (values: WizardValues): boolean => {
  if (!values.healthDeclarationRequired) return true;
  const everyConditionAnswered = values.healthDeclarationAnswers.every((a) => {
    if (a.answer === 'YES') return true;
    if (a.answer === 'NO') return a.statement.trim() !== '';
    return false;
  });
  return everyConditionAnswered && values.studentSignatureName.trim() !== '' && values.passportNumber.trim() !== '';
};
